import { useMemo } from 'react';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { MEDIA_DOWNLOAD_FEATURE } from '@/constants/media-features';

export type MediaPermissions = {
  canRead: boolean;
  canApprove: boolean;
  canDeleteAsset: boolean;
  canDelete: boolean;
  canUpdate: boolean;
  canUpload: boolean;
  canDownload: boolean;
  /** Open control-preview / preview modal (Oso `preview` on Media). */
  canPreview: boolean;
  /** Take to air / device play (Oso `play` on Media). */
  canPlay: boolean;
};

/**
 * Hook to get media permissions from MediaDto
 * Assumes permissions are included in the MediaDto response (even if not typed)
 * @param media - The media item to check permissions for
 * @returns Object with permission flags
 */
export function useMediaPermissions(media: MediaDto | undefined): MediaPermissions {
  const { hasAccess: hasDownloadFeature } = useFeatureAccess(MEDIA_DOWNLOAD_FEATURE);

  return useMemo(() => {
    if (!media) {
      return {
        canRead: false,
        canApprove: false,
        canDeleteAsset: false,
        canDelete: false,
        canUpdate: false,
        canUpload: false,
        canDownload: false,
        canPreview: false,
        canPlay: false,
      };
    }

    // Check if permissions are included in media (might be present but not typed)
    const mediaWithPermissions = media as MediaDto & {
      permissions?: string[];
      canApprove?: boolean;
      canDeleteAsset?: boolean;
      canDelete?: boolean;
      canUpdate?: boolean;
      canUpload?: boolean;
      canDownload?: boolean;
      canPreview?: boolean;
      canPlay?: boolean;
    };

    const resolveCanDownload = (baseCanDownload: boolean) => baseCanDownload && hasDownloadFeature;

    const resolveCanPreview = (permissions: string[] | undefined) =>
      (permissions?.includes('preview') || permissions?.includes('play')) ?? false;

    // If explicit permission flags exist, use them
    if (mediaWithPermissions.canApprove !== undefined) {
      const permissions = mediaWithPermissions.permissions;
      return {
        canRead: permissions?.includes('read') ?? true,
        canApprove: mediaWithPermissions.canApprove ?? false,
        canDeleteAsset: mediaWithPermissions.canDeleteAsset ?? false,
        canDelete: mediaWithPermissions.canDelete ?? false,
        canUpdate: mediaWithPermissions.canUpdate ?? false,
        canUpload: mediaWithPermissions.canUpload ?? false,
        canDownload: resolveCanDownload(mediaWithPermissions.canDownload ?? false),
        canPreview: mediaWithPermissions.canPreview ?? resolveCanPreview(permissions),
        canPlay: mediaWithPermissions.canPlay ?? permissions?.includes('play') ?? false,
      };
    }

    // Otherwise, check permissions array if it exists
    if (mediaWithPermissions.permissions) {
      return {
        canRead: mediaWithPermissions.permissions.includes('read'),
        canApprove: mediaWithPermissions.permissions.includes('approve_asset'),
        canDeleteAsset: mediaWithPermissions.permissions.includes('delete_asset'),
        canDelete: mediaWithPermissions.permissions.includes('delete'),
        canUpdate: mediaWithPermissions.permissions.includes('update'),
        canUpload: mediaWithPermissions.permissions.includes('upload_asset'),
        canDownload: resolveCanDownload(mediaWithPermissions.permissions.includes('download_asset')),
        canPreview: resolveCanPreview(mediaWithPermissions.permissions),
        canPlay: mediaWithPermissions.permissions.includes('play'),
      };
    }

    // Default: deny all (conservative approach - backend enforces permissions on API calls)
    return {
      canRead: false,
      canApprove: false,
      canDeleteAsset: false,
      canDelete: false,
      canUpdate: false,
      canUpload: false,
      canDownload: false,
      canPreview: false,
      canPlay: false,
    };
  }, [hasDownloadFeature, media]);
}
